import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { convertHTMLToRichText, convertRichTextToHTML } from 'seagull/utils/richText';
import { ExtractedServiceModel } from '~/models/ExtractedServiceModel';
import { MatchConfidence } from '~/models/matchConfidence';
import * as serviceCostModel from '~/models/service-cost';
import * as serviceTemplateModel from '~/models/service-template';
const trimToUndefined = (value)=>{
    if (value == null) return undefined;
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
};
const sanitizeClient = (client)=>{
    if (client.matchStatus === 'matched') return client;
    const companyName = trimToUndefined(client.companyName);
    const firstName = trimToUndefined(client.firstName);
    const lastName = trimToUndefined(client.lastName);
    const email = trimToUndefined(client.email);
    const phone = trimToUndefined(client.phone);
    return _object_spread({
        matchStatus: 'unmatched'
    }, companyName != null ? {
        companyName
    } : {}, firstName != null ? {
        firstName
    } : {}, lastName != null ? {
        lastName
    } : {}, email != null ? {
        email
    } : {}, phone != null ? {
        phone
    } : {});
};
const validEffectiveDate = (date)=>{
    if (date == null) return undefined;
    if (!Number.isInteger(date.year) || date.year < 1900 || date.year > 2100) return undefined;
    if (!Number.isInteger(date.month) || date.month < 1 || date.month > 12) return undefined;
    if (date.day != null && (!Number.isInteger(date.day) || date.day < 1 || date.day > 31)) return undefined;
    return date;
};
export class AgreementImportExtractionModel {
    static fromDTO(dto) {
        const legalTermsDTO = dto.legalTerms;
        let legalTerms;
        let extractedDocSnapshot = null;
        if (legalTermsDTO != null) {
            if (legalTermsDTO.matchStatus === 'matched') {
                legalTerms = {
                    matchStatus: 'matched',
                    matchedLegalTermsId: legalTermsDTO.matchedLegalTermsId,
                    matchConfidence: legalTermsDTO.matchConfidence
                };
            } else {
                const contentDelta = convertHTMLToRichText(legalTermsDTO.content);
                legalTerms = {
                    matchStatus: 'unmatched',
                    name: legalTermsDTO.name,
                    contentDelta
                };
                extractedDocSnapshot = {
                    name: legalTermsDTO.name,
                    contentDelta
                };
            }
        }
        return new AgreementImportExtractionModel({
            agreementName: dto.agreementName,
            effectiveDate: dto.effectiveDate,
            netTerms: dto.netTerms,
            hasClientSignature: dto.hasClientSignature,
            client: dto.client,
            services: dto.services,
            legalTerms,
            uncertainFields: dto.uncertainFields,
            extractedDocSnapshot
        });
    }
    toDTO() {
        var _this_data_agreementName;
        const lt = this.data.legalTerms;
        let legalTerms;
        if (lt != null) {
            if (lt.matchStatus === 'matched') {
                legalTerms = {
                    matchStatus: 'matched',
                    matchedLegalTermsId: lt.matchedLegalTermsId,
                    matchConfidence: lt.matchConfidence
                };
            } else {
                legalTerms = {
                    matchStatus: 'unmatched',
                    name: lt.name,
                    content: convertRichTextToHTML(JSON.stringify(lt.contentDelta), {
                        templateVariableMode: 'inline'
                    })
                };
            }
        }
        const trimmedAgreementName = (_this_data_agreementName = this.data.agreementName) === null || _this_data_agreementName === void 0 ? void 0 : _this_data_agreementName.trim();
        return {
            agreementName: trimmedAgreementName != null && trimmedAgreementName.length > 0 ? trimmedAgreementName : undefined,
            effectiveDate: validEffectiveDate(this.data.effectiveDate),
            netTerms: this.data.netTerms,
            hasClientSignature: this.data.hasClientSignature,
            client: sanitizeClient(this.data.client),
            services: this.data.services,
            legalTerms,
            uncertainFields: this.data.uncertainFields
        };
    }
    clone() {
        return new AgreementImportExtractionModel(_object_spread({}, this.data));
    }
    agreementName() {
        var _this_data_agreementName;
        return (_this_data_agreementName = this.data.agreementName) !== null && _this_data_agreementName !== void 0 ? _this_data_agreementName : '';
    }
    effectiveDate() {
        var _d_day;
        const d = this.data.effectiveDate;
        if (d == null) return null;
        return new Date(d.year, d.month - 1, (_d_day = d.day) !== null && _d_day !== void 0 ? _d_day : 1);
    }
    paymentTermsDisplay(triageModal) {
        const n = this.data.netTerms;
        if (n == null) return null;
        if (n === 0) return triageModal.paymentTermsDueOnReceipt();
        return triageModal.paymentTermsDueIn({
            count: n
        });
    }
    hasClientSignature() {
        return this.data.hasClientSignature;
    }
    netTerms() {
        return this.data.netTerms;
    }
    client() {
        return this.data.client;
    }
    services() {
        return this.data.services;
    }
    legalTerms() {
        const lt = this.data.legalTerms;
        if (lt == null) return undefined;
        if (lt.matchStatus === 'matched') {
            return {
                matchStatus: 'matched',
                matchedLegalTermsId: lt.matchedLegalTermsId,
                matchConfidence: lt.matchConfidence
            };
        }
        return {
            matchStatus: 'unmatched',
            name: lt.name,
            content: convertRichTextToHTML(JSON.stringify(lt.contentDelta), {
                templateVariableMode: 'inline'
            })
        };
    }
    setAgreementName(name) {
        return this.cloneWithChanges({
            agreementName: name || undefined
        });
    }
    setNetTerms(netTerms) {
        return this.cloneWithChanges({
            netTerms
        });
    }
    setEffectiveDate(date) {
        const effectiveDate = date == null ? undefined : {
            year: date.getFullYear(),
            month: date.getMonth() + 1,
            day: date.getDate()
        };
        return this.cloneWithChanges({
            effectiveDate
        });
    }
    setContactField(field, value) {
        const client = _object_spread_props(_object_spread({}, this.data.client), {
            [field]: value
        });
        return this.cloneWithChanges({
            client
        });
    }
    matchContactToExisting(contactId, copied) {
        const client = {
            matchStatus: 'matched',
            matchedContactId: contactId,
            matchConfidence: MatchConfidence.High,
            companyName: copied.companyName,
            firstName: copied.firstName,
            lastName: copied.lastName,
            email: copied.email,
            phone: copied.phone
        };
        return this.cloneWithChanges({
            client
        });
    }
    unmatchContact() {
        const client = {
            matchStatus: 'unmatched'
        };
        return this.cloneWithChanges({
            client
        });
    }
    contactHasAllRequiredFields(validators) {
        const c = this.data.client;
        if (!c.companyName || !c.firstName || !c.lastName || !c.email) return false;
        return validators.getEmailValidator()(c.email) === true;
    }
    hasContactDetails() {
        const c = this.data.client;
        return Boolean(c.companyName || c.firstName || c.lastName || c.email || c.phone);
    }
    isContactUnmatched() {
        return this.data.client.matchStatus === 'unmatched';
    }
    serviceModels() {
        return this.data.services.map((service)=>new ExtractedServiceModel(service));
    }
    removeService(index) {
        const services = this.data.services.filter((_, i)=>i !== index);
        return this.cloneWithChanges({
            services
        });
    }
    replaceServiceWithTemplate(index, template) {
        const existing = this.data.services[index];
        if (existing.matchStatus !== 'matched') {
            throw new Error('replaceServiceWithTemplate requires a matched service');
        }
        const pricing = mapPricing(template.cost);
        const trigger = mapTrigger(template.billing, pricing);
        const replaced = {
            name: template.name,
            billingTrigger: trigger,
            matchStatus: 'matched',
            matchedServiceTemplateId: template.id,
            matchedServiceTemplateName: template.name,
            matchConfidence: MatchConfidence.High
        };
        const services = this.data.services.map((service, i)=>i === index ? replaced : service);
        return this.cloneWithChanges({
            services
        });
    }
    reorderServices(reordered) {
        return this.cloneWithChanges({
            services: reordered
        });
    }
    switchServiceToFileVersion(index) {
        const existing = this.data.services[index];
        const replaced = {
            name: existing.name,
            billingTrigger: existing.billingTrigger,
            matchStatus: 'unmatched'
        };
        const services = this.data.services.map((service, i)=>i === index ? replaced : service);
        return this.cloneWithChanges({
            services
        });
    }
    isLegalTermsUnmatched() {
        var _this_data_legalTerms;
        return ((_this_data_legalTerms = this.data.legalTerms) === null || _this_data_legalTerms === void 0 ? void 0 : _this_data_legalTerms.matchStatus) === 'unmatched';
    }
    legalTermsHasName() {
        const lt = this.data.legalTerms;
        return (lt === null || lt === void 0 ? void 0 : lt.matchStatus) === 'unmatched' && lt.name.trim() !== '';
    }
    legalTermsContentDelta() {
        const lt = this.data.legalTerms;
        if ((lt === null || lt === void 0 ? void 0 : lt.matchStatus) !== 'unmatched') return null;
        return lt.contentDelta;
    }
    legalTermsSelectedId() {
        const lt = this.data.legalTerms;
        return (lt === null || lt === void 0 ? void 0 : lt.matchStatus) === 'matched' ? lt.matchedLegalTermsId : undefined;
    }
    extractedDocSnapshot() {
        return this.data.extractedDocSnapshot;
    }
    setLegalTermsName(name) {
        const lt = this.data.legalTerms;
        if ((lt === null || lt === void 0 ? void 0 : lt.matchStatus) !== 'unmatched') return this;
        return this.cloneWithChanges({
            legalTerms: _object_spread_props(_object_spread({}, lt), {
                name
            }),
            extractedDocSnapshot: {
                name,
                contentDelta: lt.contentDelta
            }
        });
    }
    setLegalTermsContent(contentDelta) {
        const lt = this.data.legalTerms;
        if ((lt === null || lt === void 0 ? void 0 : lt.matchStatus) !== 'unmatched') return this;
        return this.cloneWithChanges({
            legalTerms: _object_spread_props(_object_spread({}, lt), {
                contentDelta
            }),
            extractedDocSnapshot: {
                name: lt.name,
                contentDelta
            }
        });
    }
    matchLegalTermsToExisting(id) {
        return this.cloneWithChanges({
            legalTerms: {
                matchStatus: 'matched',
                matchedLegalTermsId: id,
                matchConfidence: MatchConfidence.High
            }
        });
    }
    revertToExtractedSnapshot() {
        const snap = this.data.extractedDocSnapshot;
        if (snap == null) return this;
        return this.cloneWithChanges({
            legalTerms: {
                matchStatus: 'unmatched',
                name: snap.name,
                contentDelta: snap.contentDelta
            }
        });
    }
    cloneWithChanges(partial) {
        return new AgreementImportExtractionModel(_object_spread({}, this.data, partial));
    }
    constructor(data){
        _define_property(this, "data", void 0);
        this.data = data;
    }
}
function mapPricing(cost) {
    return serviceCostModel.match(cost, {
        fixed: (fixed)=>({
                type: 'fixed',
                price: fixed.price.toNumber(),
                discount: {
                    type: 'none'
                }
            }),
        variable: (variable)=>{
            var _variable_unitName;
            return {
                type: 'variable',
                unitPrice: variable.price.toNumber(),
                unitName: (_variable_unitName = variable.unitName) !== null && _variable_unitName !== void 0 ? _variable_unitName : undefined,
                cap: variable.unitCap != null ? variable.unitCap.toNumber() : undefined,
                discount: {
                    type: 'none'
                }
            };
        },
        range: (range)=>({
                type: 'range',
                minPrice: range.minPrice != null ? range.minPrice.toNumber() : undefined,
                maxPrice: range.maxPrice.toNumber(),
                discount: {
                    type: 'none'
                }
            })
    });
}
function mapTrigger(billing, pricing) {
    return serviceTemplateModel.matchBilling(billing, {
        ongoing: (ongoing)=>({
                type: 'automaticallyRecurring',
                recurrencePeriod: ongoing.recurrencePeriod,
                pricing
            }),
        repeatableManual: (repeatableManual)=>({
                type: 'manuallyRecurring',
                recurrencePeriod: repeatableManual.recurrencePeriod,
                pricing
            }),
        oneTimeManual: ()=>({
                type: 'oneTimeManual',
                pricing
            }),
        onApproval: ()=>({
                type: 'oneTimeOnApproval',
                pricing
            }),
        onDemand: ()=>({
                type: 'onDemand',
                pricing
            }),
        oneTimeInStages: (param)=>{
            let { oneTimeInStages } = param;
            return {
                type: 'oneTimeInStages',
                valueType: oneTimeInStages.valueType(),
                stages: oneTimeInStages.stages().map((s)=>({
                        stageNumber: s.stageNumber,
                        value: s.value.toNumber()
                    })),
                pricing
            };
        }
    });
}
